import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Link,
  useColorModeValue,
  IconButton,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { FiX } from 'react-icons/fi';
import IconWrapper from './IconWrapper';

declare global {
  interface Window {
    gtag?: (command: string, action: string, params?: Record<string, any>) => void;
  }
}

const COOKIE_CONSENT_KEY = 'cookie_consent_accepted';

export const CookieConsent: React.FC = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  
  // Hooks must be called before any early returns
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.900', 'white');
  const textSecondary = useColorModeValue('gray.600', 'gray.400');

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      setIsVisible(true);
    }
  }, []);

  const handleAccept = (): void => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    setIsVisible(false);
  };

  const handleDecline = (): void => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'declined');
    setIsVisible(false);
    // Disable analytics if declined
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
      });
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Box
      position="fixed"
      bottom={0}
      left={0}
      right={0}
      zIndex={9999}
      bg={bg}
      borderTop="1px"
      borderColor={borderColor}
      boxShadow="lg"
      p={4}
      pb={{ base: '80px', md: 4 }} // Add bottom padding on mobile to account for bottom navigation
    >
      <HStack
        maxW="1200px"
        mx="auto"
        spacing={4}
        align="flex-start"
        flexDirection={{ base: 'column', md: 'row' }}
        position="relative"
      >
        <IconButton
          aria-label="Close cookie consent"
          icon={<IconWrapper icon={FiX} size={18} />}
          position="absolute"
          top={0}
          right={0}
          size="sm"
          variant="ghost"
          onClick={handleDecline}
          display={{ base: 'flex', md: 'none' }} // Show close button on mobile only
        />
        <VStack align="flex-start" flex={1} spacing={2} pr={{ base: 8, md: 0 }}>
          <Text fontWeight="bold" color={textColor}>
            Cookie Consent
          </Text>
          <Text fontSize="sm" color={textSecondary}>
            We use cookies to enhance your browsing experience, analyze site traffic, and personalize content.
            By clicking "Accept", you consent to our use of cookies.{' '}
            <Link
              onClick={() => navigate('/privacy')}
              color="purple.500"
              textDecoration="underline"
              cursor="pointer"
              _hover={{ color: 'purple.600' }}
            >
              Learn more
            </Link>
          </Text>
        </VStack>
        <HStack spacing={2} w={{ base: '100%', md: 'auto' }}>
          <Button
            size="sm"
            variant="outline"
            onClick={handleDecline}
            leftIcon={<IconWrapper icon={FiX} size={16} />}
            flex={{ base: 1, md: 'none' }}
          >
            Decline
          </Button>
          <Button
            size="sm"
            colorScheme="purple"
            onClick={handleAccept}
            flex={{ base: 1, md: 'none' }}
          >
            Accept
          </Button>
        </HStack>
      </HStack>
    </Box>
  );
};

